import { gql } from '@apollo/client';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import ResourcesTable from './ResourcesTable';
import { metricsQueryString } from '../../utils/utils';
import { ControlledTabs } from '../basics/Tabs';
import { useSetBreadcrumbEffect } from '../../contexts/BreadcrumbContext';
import {
  INPUT_TYPE_FUEL,
  INPUT_TYPE_MATERIALS,
  useInputTypeContext,
} from '../../contexts/InputTypeContext';
import { timeColumns } from '../../utils/columns';
import { getFirstQueryResult } from '../../utils/graphql';
import { asCheck } from '../../utils/formatters';

const TABS = [
  { display: INPUT_TYPE_MATERIALS, value: INPUT_TYPE_MATERIALS },
  { display: INPUT_TYPE_FUEL, value: INPUT_TYPE_FUEL },
];

const COLS = [
  {
    accessor: 'name',
    displayString: 'Name',
  },
  ...timeColumns,
  {
    accessor: 'inUse',
    displayString: 'In Use',
    format: asCheck,
    style: { verticalAlign: 'middle' },
    isSmall: true,
  },
];

export const InputResourcesQuery = gql`
  query InputResources {
    inputResources {
      id
      ${metricsQueryString(COLS)}
    }
  }
`;

const TeamInputResourcesQuery = gql`
  query TeamInputResources($id: ID!) {
    team(id: $id) {
      id
      inputResources {
        id
        ${metricsQueryString(COLS)}
      }
    }
  }
`;

export const RawInputsQuery = gql`
  query RawInputs($farmId: ID!) {
    rawInputs(farmId: $farmId) {
      id
      ${metricsQueryString(COLS)}
    }
  }
`;

export const FuelPricingResourcesQuery = gql`
  query FuelPricingResources {
    fuelResources {
      id
      pricePerUnit
      ${metricsQueryString(COLS)}
    }
  }
`;

const TeamPricingResourcesQuery = gql`
  query TeamPricingResourcesQuery($id: ID!) {
    team(id: $id) {
      id
      pricingResources {
        id
        pricePerUnit
        ${metricsQueryString(COLS)}
      }
    }
  }
`;

export const RawFuelPricingsQuery = gql`
  query RawFuelPricings($farmId: ID!) {
    rawFuels(farmId: $farmId) {
      id
      pricePerUnit
      ${metricsQueryString(COLS)}
    }
  }
`;

const InputsTitle = styled.h1`
  margin: var(--header-margin-y) var(--cell-padding) 16px;
`;

const InputsTable = ({
  InputsQuery,
  PricingsQuery,
  getInputsResult,
  getPricingsResult,
  queryOptions,
}) => {
  const { teamId } = useParams();
  const { type, setSelectedType } = useInputTypeContext();

  const breadcrumbs = [{ display: type }];
  useSetBreadcrumbEffect(breadcrumbs, [type, teamId]);

  return (
    <>
      <InputsTitle>Materials & Fuel</InputsTitle>
      <ControlledTabs
        tabs={TABS}
        activeTab={type}
        onTabChange={(tab) => setSelectedType(tab)}
      >
        {(tab) => (
          <>
            {tab === INPUT_TYPE_MATERIALS && (
              <ResourcesTable
                cols={COLS}
                getQueryResult={getInputsResult}
                newText="New Input"
                query={InputsQuery}
                queryOptions={queryOptions}
                sortColumn={['inUse', 'name']}
                sortDirection={['desc', 'asc']}
              />
            )}
            {tab === INPUT_TYPE_FUEL && (
              <ResourcesTable
                cols={COLS}
                getQueryResult={getPricingsResult}
                newText="New Input"
                query={PricingsQuery}
                queryOptions={queryOptions}
                urlPrefix="pricing/"
                sortColumn={['inUse', 'name']}
                sortDirection={['desc', 'asc']}
              />
            )}
          </>
        )}
      </ControlledTabs>
    </>
  );
};

export const InputResourcesTable = () => (
  <InputsTable
    InputsQuery={InputResourcesQuery}
    PricingsQuery={FuelPricingResourcesQuery}
  />
);

export const TeamInputResourcesTable = ({ teamId }) => (
  <InputsTable
    InputsQuery={TeamInputResourcesQuery}
    PricingsQuery={TeamPricingResourcesQuery}
    getInputsResult={(data) => getFirstQueryResult(data)?.inputResources}
    getPricingsResult={(data) => getFirstQueryResult(data)?.pricingResources}
    queryOptions={{ variables: { id: teamId } }}
  />
);

export const FarmInputsTable = () => (
  <InputsTable
    InputsQuery={RawInputsQuery}
    PricingsQuery={RawFuelPricingsQuery}
  />
);
